import type { ThemeOptions } from "@mui/material/styles";

import {
  fluidFontSize,
  fontSize,
  fontStack,
  fontWeight,
  letterSpacing,
  lineHeight,
  weave,
} from "@app/theme/weaveTokens.ts";

const fontFamilies = {
  default: fontStack,
  display: weave.generic.font["artifakt-legend"],
  code: weave.generic.font.courier,
};

const variantStyle = (
  family: string,
  size: string,
  weight: number,
  height: string,
  spacing: string = letterSpacing.none
) => ({
  fontFamily: family,
  fontSize: size,
  fontWeight: weight,
  letterSpacing: spacing,
  lineHeight: height,
});

const { default: text, display, code } = fontFamilies;
const { regular, medium, bold, extraBold } = fontWeight;
const { small, medium: mid, large } = fontSize;
const { tight, compact, default: loose } = lineHeight;

/**
 * The typography variants the app and the remaining DHIG components use, with
 * DHIG's values. The font family keeps the `Arial, sans-serif` fallback, and the
 * fluid sizes keep DHIG's slope, which differs slightly from Weave's
 * (LESSONS R5, DEVX-11164). Sizes that Weave has an exact token for use it.
 */
export const typographyVariant = {
  "display-extra": variantStyle(display, fluidFontSize[10], extraBold, tight),
  display: variantStyle(display, fluidFontSize[9], extraBold, tight),
  "headline-largest": variantStyle(display, fluidFontSize[8], extraBold, tight),
  "headline-larger": variantStyle(display, fluidFontSize[7], extraBold, tight),
  "headline-large": variantStyle(display, fluidFontSize[6], extraBold, tight),
  "headline-medium": variantStyle(display, fluidFontSize[5], extraBold, tight),
  "headline-small": variantStyle(text, fluidFontSize[4], bold, compact),
  "headline-smaller": variantStyle(text, large, bold, loose),
  "headline-smallest": variantStyle(text, mid, bold, loose),
  "body-copy-large": variantStyle(text, fluidFontSize[4], regular, loose),
  "body-copy-medium": variantStyle(text, large, regular, loose),
  "body-copy-small": variantStyle(text, mid, regular, loose),
  "short-copy-large": variantStyle(text, fluidFontSize[4], regular, compact),
  "short-copy-medium": variantStyle(text, large, regular, compact),
  "short-copy-small": variantStyle(text, mid, regular, compact),
  smallprint: variantStyle(text, small, regular, loose),
  code: variantStyle(code, mid, regular, loose),
  caption: variantStyle(text, mid, regular, compact),
  eyebrow: {
    ...variantStyle(text, mid, regular, loose, letterSpacing.loose),
    textTransform: "uppercase" as const,
  },
  "pull-quote-medium": variantStyle(text, fluidFontSize[4], medium, compact),
  "pull-quote-large": variantStyle(text, fluidFontSize[5], medium, compact),
  "input-value": variantStyle(text, large, medium, compact),
  "button-label": variantStyle(text, large, medium, compact),
  "button-label-dense": variantStyle(text, mid, medium, compact),
};

/**
 * MUI variants that Weave defines with `ArtifaktElement` and no fallback. They
 * keep Weave's sizes and get the full stack.
 */
const muiVariantsWithFallback = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "subtitle1",
  "subtitle2",
  "body2",
  "button",
  "caption",
  "overline",
];

/**
 * DHIG maps MUI's font weight scale onto its own weights (medium is 700, bold
 * is 800, against MUI's 500 and 700). Components such as TableCell heads read
 * `typography.fontWeightMedium`, so without this the table header labels lose
 * their weight.
 *
 * It also carries the variants above and `body1` as DHIG maps it. Prefer the
 * explicit `fontWeight` tokens in new code over `fontWeightMedium` (Weave names
 * 600 "medium").
 */
export const appTypography = {
  ...typographyVariant,
  body1: typographyVariant["body-copy-medium"],
  ...Object.fromEntries(
    muiVariantsWithFallback.map((variant) => [
      variant,
      { fontFamily: fontStack },
    ])
  ),
  fontFamily: fontStack,
  fontWeightLight: regular,
  fontWeightRegular: regular,
  fontWeightMedium: bold,
  fontWeightBold: extraBold,
};

/**
 * Weave pins `fontFamily: "ArtifaktElement"` (no fallback) in 20-odd component
 * slots, such as Button, MenuItem, Chip, Tooltip, Tabs, Input, Alert and the
 * form control labels. The app uses the DHIG stack, so if the webfont is slow or
 * blocked those components would fall back to the browser default (usually
 * serif). This walks Weave's `styleOverrides` and returns the same paths with
 * the full stack, to merge over Weave's theme. It only reads object slots (a
 * function slot cannot be inspected), and none of Weave's function slots set a
 * font family. Walking the theme keeps this working after a Weave upgrade
 * (LESSONS R11).
 */
export const fontFallbackOverrides = (
  weaveComponents: ThemeOptions["components"]
): ThemeOptions["components"] => {
  const pinned = (node: unknown): Record<string, unknown> | undefined => {
    if (!node || typeof node !== "object" || Array.isArray(node)) {
      return undefined;
    }
    const found: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(node)) {
      if (key === "fontFamily" && value === weave.generic.font.family) {
        found[key] = fontStack;
      } else {
        const child = pinned(value);
        if (child) found[key] = child;
      }
    }
    return Object.keys(found).length > 0 ? found : undefined;
  };
  const overrides: Record<string, unknown> = {};
  for (const [name, component] of Object.entries(weaveComponents ?? {})) {
    const styleOverrides = pinned(component?.styleOverrides);
    if (styleOverrides) overrides[name] = { styleOverrides };
  }
  return overrides as ThemeOptions["components"];
};
